/* ==========================================================================
   Portal Slug Utilities
   Resolve the current pharmacy portal slug from the URL and build per-slug
   storage keys.

   The same email can be a separate account in multiple pharmacies, so provider
   and pharmacy-admin auth tokens are stored per portal slug (e.g.
   `tjm_access_token:acme`, `tjm_admin_token:acme`) instead of a single global
   key. This lets a user be logged into several `/rx/:slug` portals at once. The
   axios clients are module singletons (no React hooks), so they read the slug
   from the URL at request time via getCurrentSlug().
   ========================================================================== */

import { safeGetItem, safeSetItem, safeRemoveItem } from './safeStorage'

/**
 * Global hint naming the pharmacy-admin portal most recently verified in this
 * browser (written by AdminAuthContext only after the stored token's
 * `pharmacy_id` matched the pharmacy at that slug). The no-slug Landing page
 * uses it to find which per-slug admin session to show, and the deploy-time
 * migration uses it to bind the legacy bare-key admin token to its portal.
 */
export const ADMIN_PHARMACY_SLUG_KEY = 'tjm_admin_pharmacy_slug'

/**
 * Extract the current pharmacy portal slug from the URL path.
 *
 * Both the provider portal (`/rx/:slug/...`) and the admin portal
 * (`/rx/:slug/admin/...`) live under `/rx/:slug`, so the same matcher serves
 * both. Returns null for global, non-portal pages (e.g. `/`, `/admin/login`,
 * `/internal/login`).
 */
export function getCurrentSlug(): string | null {
  const match = window.location.pathname.match(/^\/rx\/([^/]+)/)
  if (!match) return null
  let slug = match[1]
  // Decode percent-encoding and lower-case so the value matches the slug the
  // backend stored (slugs are generated lower-case). Without this, a URL like
  // `/rx/Acme` would be sent verbatim and fail the exact-match pharmacy lookup,
  // 401-ing a valid login, and would key storage under a different casing than
  // the canonical route.
  try {
    slug = decodeURIComponent(slug)
  } catch {
    // Malformed percent-encoding — fall back to the raw segment.
  }
  return slug.toLowerCase()
}

/**
 * Build a storage key scoped to a pharmacy portal slug.
 *
 * Falls back to the bare base key when no slug is available (the global no-slug
 * pages), preserving behavior for flows that run outside a `/rx/:slug` route.
 *
 * The slug is lower-cased here so this is the single chokepoint for casing: a
 * caller passing an explicit mixed-case slug (e.g. an upper-cased `portal_slug`
 * in a request body, or a mixed-case admin-login URL) stores and reads under the
 * same key as the lower-cased `getCurrentSlug()` the interceptors use, instead
 * of splitting store-vs-read keys and silently logging the user out.
 */
export function keyFor(base: string, slug: string | null = getCurrentSlug()): string {
  return slug ? `${base}:${slug.toLowerCase()}` : base
}

/**
 * Inject the current portal slug into an auth request body when the caller did
 * not set one explicitly.
 *
 * Centralises the `|| getCurrentSlug() || undefined` rule that login,
 * forgot-password, and their admin equivalents all need, so the fallback can
 * never drift between call sites. `undefined` (not `null`) is used so the key is
 * omitted from the JSON body on the no-slug global pages.
 *
 * Uses `||` rather than `??` so a blank/empty-string slug also falls back to the
 * URL slug: an empty string is falsy in `keyFor`, so it would be stored under the
 * bare key while the request interceptor reads the per-slug key — silently
 * logging the user out right after a successful login.
 */
export function withPortalSlug<T extends { portal_slug?: string }>(request: T): T {
  return { ...request, portal_slug: request.portal_slug || getCurrentSlug() || undefined }
}

/* --------------------------------------------------------------------------
   Legacy token migration

   Auth tokens used to live under bare keys (e.g. `tjm_access_token`); they are
   now scoped per portal slug (`tjm_access_token:<slug>`). On deploy, an
   already-logged-in user's bare-key token would no longer be read, so without a
   migration every provider and pharmacy-admin would be forced to re-login.
   `migrateLegacyTokens()` instead moves each bare-key token (with its expiry +
   remember-me) into the per-slug namespace so existing sessions survive.

   Only the provider and pharmacy-admin keys are namespaced; internal/super-admin
   tokens (`tjm_internal_token*`) are global and intentionally left untouched.
   -------------------------------------------------------------------------- */

/**
 * A pre-namespacing auth key triple. The three keys must always migrate
 * together under the same slug: session restore reads token → expiry →
 * remember-me, so a token moved without its expiry would read as expired and
 * log a non-remember-me user out.
 */
interface LegacyAuthGroup {
  token: string
  expiry: string
  rememberMe: string
}

/** The pre-namespacing auth keys, grouped — the single source of truth.
 *  Provider (index 0) and pharmacy-admin (index 1) tokens are namespaced per
 *  slug; only internal/super-admin tokens (`tjm_internal_token*`) stay global
 *  and are intentionally left untouched. */
const LEGACY_AUTH_GROUPS: readonly LegacyAuthGroup[] = [
  // Provider
  {
    token: 'tjm_access_token',
    expiry: 'tjm_token_expires_at',
    rememberMe: 'tjm_remember_me',
  },
  // Pharmacy admin (PORTAL-174)
  {
    token: 'tjm_admin_token',
    expiry: 'tjm_admin_token_expires_at',
    rememberMe: 'tjm_admin_remember_me',
  },
] as const

/**
 * One-shot flag that bounds the deploy-time migration so it does not stay in the
 * boot path forever. Set once no bare-key tokens remain (all migrated, adopted,
 * or expired); thereafter `migrateLegacyTokens()` returns immediately.
 *
 * v2: the v1 flag was set while only the provider token was namespaced, so in
 * most browsers it is already present and would short-circuit the migration
 * before the pharmacy-admin triple (namespaced later, PORTAL-174) is ever seen.
 */
const MIGRATION_DONE_KEY = 'tjm_token_migration_v2_done'
const LEGACY_MIGRATION_DONE_KEY_V1 = 'tjm_token_migration_v1_done'

/**
 * Decode a JWT payload into an object, or null if undecodable.
 *
 * JWT segments are base64**url** (`-`/`_` instead of `+`/`/`, padding stripped),
 * which `atob` does not accept — feeding it a raw segment throws on any token
 * whose payload contains those characters. Translate to standard base64 and
 * re-pad first. Exported so every JWT reader shares one correct decoder.
 */
export function decodeJwtPayload<T = Record<string, unknown>>(
  token: string,
): T | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    let seg = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = seg.length % 4
    if (pad) seg += '='.repeat(4 - pad)
    // `atob` yields one byte per char (latin1); decode those bytes as UTF-8 so a
    // multibyte claim (e.g. a non-ASCII name) is not mangled into mojibake.
    const bytes = atob(seg)
    const json = decodeURIComponent(
      Array.from(bytes, (c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')
    )
    return JSON.parse(json) as T
  } catch {
    return null
  }
}

/** Decode a JWT's `exp` claim (unix seconds), or null if undecodable. */
function decodeJwtExp(token: string): number | null {
  const payload = decodeJwtPayload<{ exp?: unknown }>(token)
  return payload && typeof payload.exp === 'number' ? payload.exp : null
}

/** ISO-8601 expiry string derived from a JWT's `exp` claim, or null. */
function expiryIsoFromToken(token: string): string | null {
  const exp = decodeJwtExp(token)
  return exp !== null ? new Date(exp * 1000).toISOString() : null
}

/**
 * Whether a bare-key token is already expired. Trusts the stored expiry string
 * (what `isTokenExpired()` actually checks) first, then the JWT `exp` claim.
 * Returns false when expiry can't be determined, so a maybe-valid token is
 * never destroyed.
 */
function isBareTokenExpired(token: string, group: LegacyAuthGroup): boolean {
  const storedExpiry = safeGetItem(group.expiry)
  if (storedExpiry) {
    const ms = Date.parse(storedExpiry)
    if (!Number.isNaN(ms)) return Date.now() >= ms
  }
  const exp = decodeJwtExp(token)
  if (exp !== null) return Date.now() >= exp * 1000
  return false
}

/** Remove all three bare keys of a group. */
function removeBareKeys(group: LegacyAuthGroup): void {
  safeRemoveItem(group.token)
  safeRemoveItem(group.expiry)
  safeRemoveItem(group.rememberMe)
}

/**
 * Migrate one bare-key auth triple into the per-slug namespace.
 *
 * If the slug is known, copy token + expiry + remember-me to their `:<slug>`
 * keys (without clobbering a newer per-slug login) and drop the bare keys. If
 * the slug is unknown, keep the bare keys so a later `/rx/:slug` visit can
 * migrate them — unless the token is already expired, in which case clean up.
 */
function migrateGroup(group: LegacyAuthGroup, slug: string | null): void {
  const bareToken = safeGetItem(group.token)
  if (bareToken === null) return

  // Truthy check (not `!== null`) to match `keyFor`: an empty-string slug would
  // resolve to the bare key itself, making `existing === bareToken` trivially
  // true and deleting the only copy of a live session.
  if (slug) {
    const targetKey = keyFor(group.token, slug)
    const existing = safeGetItem(targetKey)
    if (existing === null) {
      // Target slug has no token yet — migrate this one into it. Only drop the
      // bare keys once the token has actually persisted: safeSetItem silently
      // no-ops when storage is blocked/full, and deleting the source after a
      // failed write would destroy a still-valid session with no replacement.
      if (!safeSetItem(targetKey, bareToken)) return
      // Carry the expiry across; if it was never stored, derive it from the
      // token's own `exp` claim. Otherwise the migrated token has no expiry and
      // reads as expired, logging the user straight back out on this same boot.
      const expiry =
        safeGetItem(group.expiry) ?? expiryIsoFromToken(bareToken)
      if (expiry !== null) safeSetItem(keyFor(group.expiry, slug), expiry)
      const rememberMe = safeGetItem(group.rememberMe)
      if (rememberMe !== null) {
        safeSetItem(keyFor(group.rememberMe, slug), rememberMe)
      }
      removeBareKeys(group)
    } else if (existing === bareToken) {
      // Already migrated under this slug on a previous boot — drop the leftover
      // bare copy only.
      removeBareKeys(group)
    } else if (isBareTokenExpired(bareToken, group)) {
      // A different, fresher token already owns this slug AND the bare token is
      // dead — safe to clean up. If the bare token were still valid we keep it,
      // so it can migrate under its OWN portal on a later visit rather than being
      // destroyed here.
      removeBareKeys(group)
    }
    return
  }

  if (isBareTokenExpired(bareToken, group)) {
    removeBareKeys(group)
  }
}

/** Whether a pre-namespacing bare provider token is present. */
export function hasLegacyProviderToken(): boolean {
  return safeGetItem(LEGACY_AUTH_GROUPS[0].token) !== null
}

/**
 * Adopt a pre-namespacing bare provider token into a portal's per-slug namespace,
 * but ONLY after the caller has verified (via the loaded pharmacy id) that the
 * token actually belongs to this portal. Returns the adopted token, or null if
 * there is nothing to adopt or it does not belong here.
 *
 * This is the safe replacement for the old blind boot-time migration: the bare
 * provider token carries a `pharmacy_id` but not a slug, and the slug→pharmacy
 * map is not known offline, so migrating under the raw URL slug could bind one
 * pharmacy's token to another portal and destroy the real session. A token that
 * does not match is left untouched so its real portal can still adopt it later.
 */
export function adoptLegacyProviderToken(
  slug: string,
  expectedPharmacyId: string,
): string | null {
  const group = LEGACY_AUTH_GROUPS[0]
  const bareToken = safeGetItem(group.token)
  if (bareToken === null) return null

  const payload = decodeJwtPayload<{ pharmacy_id?: unknown }>(bareToken)
  if (!payload || payload.pharmacy_id !== expectedPharmacyId) {
    // Not this portal's token — leave the bare keys for the right portal.
    return null
  }
  if (isBareTokenExpired(bareToken, group)) {
    removeBareKeys(group)
    return null
  }
  const targetKey = keyFor(group.token, slug)
  if (safeGetItem(targetKey) !== null) {
    // A newer per-slug login already owns this slug — don't clobber it.
    return null
  }

  // Persist the token under the per-slug key first; only if that write actually
  // succeeded do we carry the metadata across and delete the bare keys. A
  // silently-failed write (storage blocked/full) leaves the bare keys intact so
  // the session survives for a later boot rather than being lost here.
  if (!safeSetItem(targetKey, bareToken)) return null
  const expiry =
    safeGetItem(group.expiry) ?? expiryIsoFromToken(bareToken)
  if (expiry !== null) safeSetItem(keyFor(group.expiry, slug), expiry)
  const rememberMe = safeGetItem(group.rememberMe)
  if (rememberMe !== null) {
    safeSetItem(keyFor(group.rememberMe, slug), rememberMe)
  }
  removeBareKeys(group)
  return bareToken
}

/**
 * Migrate the pre-namespacing provider auth token into the per-slug namespace so
 * logged-in providers are not forced to re-login on deploy. Idempotent and
 * bounded by `MIGRATION_DONE_KEY` so it leaves the boot path once complete.
 *
 * Provider: there is no pre-namespacing provider slug hint, and the URL slug may
 * be a *different* portal than the token's, so we never copy under a slug here
 * (that would mis-bind and destroy the session). We only clean up an
 * already-expired bare token; the live, pharmacy-id-verified adoption happens in
 * `AuthContext` (see `adoptLegacyProviderToken`).
 *
 * Pharmacy admin: unlike the provider, a verified slug hint exists —
 * `ADMIN_PHARMACY_SLUG_KEY` is only ever written after the stored token's
 * `pharmacy_id` matched the pharmacy at that slug — so the bare admin triple is
 * bound to it here at boot. Doing it before React mounts means the interceptors,
 * AdminAuthContext and the Landing page all see the migrated key on the first
 * render after deploy. If the hint is absent, `migrateGroup` only cleans up an
 * expired token and the admin re-logs in once; if it is stale (e.g. a slug
 * rename), the token lands under the wrong slug, the mount-time pharmacy-id
 * check bails, and the next login overwrites it — one forced re-login, never a
 * destroyed session. Internal/super-admin tokens stay global and are untouched.
 */
export function migrateLegacyTokens(): void {
  if (safeGetItem(MIGRATION_DONE_KEY) !== null) return

  migrateGroup(LEGACY_AUTH_GROUPS[0], null) // provider: cleanup only; adopt in AuthContext
  migrateGroup(LEGACY_AUTH_GROUPS[1], safeGetItem(ADMIN_PHARMACY_SLUG_KEY)) // admin: verified hint

  // Once no bare token remains there is nothing left to migrate, ever.
  if (LEGACY_AUTH_GROUPS.every((group) => safeGetItem(group.token) === null)) {
    safeSetItem(MIGRATION_DONE_KEY, 'true')
    safeRemoveItem(LEGACY_MIGRATION_DONE_KEY_V1)
  }
}
